import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  PLATFORM_ID,
  inject,
  DOCUMENT,
} from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { ComponentsModule } from '@freelancer/components';
import { Datastore } from '@freelancer/datastore';
import {
  CanvasCollection,
  DatastoreAiSmartCanvasMakeRealModule,
  type AiSmartCanvasMakeRealCollection,
  type PrototyperPermissionsCollection,
  type User,
  type UserInteractionsCollection,
  type UsersCollection,
  DatastoreCanvasModule,
  DatastorePrototyperPermissionsModule,
} from '@freelancer/datastore/collections';
import { Pwa } from '@freelancer/pwa';
import { ThemeService } from '@freelancer/theme';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { ModalService } from '@freelancer/ui';
import { ModalSize } from '@freelancer/ui/modal';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  CanvasContextTypeApi,
  PrototyperPermissionAccessRoleApi,
  PrototyperPermissionPrincipalTypeApi,
} from 'api-typings/canvas/canvas';
import { sha256 } from 'js-sha256';
import { filter, firstValueFrom, map } from 'rxjs';
import type {
  boardFunc,
  FuncContainer,
  GetConfigFunc,
  MakeRealFunc,
  ShowPermissionsFunc,
  TldrawWCUserProps,
} from 'tldraw-web-component';
import { v4 as uuid } from 'uuid';
import { CANVAS_CONFIG } from './canvas.config';
import { CanvasConfig } from './canvas.interface';
import { CanvasService } from './canvas.service';

@UntilDestroy({ className: 'CanvasComponent' })
@Component({
  standalone: true,
  selector: 'fl-canvas',
  styleUrls: ['./canvas.component.scss'],
  imports: [
    CommonModule,
    ComponentsModule,
    TrackingModule,
    UiModule,
    DatastoreAiSmartCanvasMakeRealModule,
    DatastoreCanvasModule,
    DatastorePrototyperPermissionsModule,
  ],
  template: `<span [id]="tldrawContainerId"></span>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * This component is used to render the canvas for the Smart Canvas Playground.
 * This component will first fetch an access token from `canvas/access_token` API endpoint.
 * The fetched access token is then used to authenticate with the canvas server.
 *
 * ### The reason for attaching functions to the window.
 *
 * Note: This component was originally a React component, but due to issues with how newer versions of React and Angular interact,
 * with some very annoying re-rendering issues and various other bugs, we decided to convert it to create a web component and use it in Angular.
 *
 * With web components, it is not as easy to pass data to the component as it is with Angular components, so we had to use global functions.
 * In pure html, you normally pass the name of the function to the attribute of the web component, and the web component will call that function.
 *
 * Angular inherently does not make it easy to pass functions into components,
 * as we don't even know the final name of the function until after it is compiled, and even then, it will be in some random chunk.
 *
 * So to keep things simple, we create global functions that the web component can call, and then we pass the name of the function to the web component.
 *
 * #### NOTES:
 * - One must remember to clean up the global functions when the component is destroyed.
 * - For documentations on the functions, see misc/tldraw-web-component/
 */
export class CanvasComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private canvasConfig = inject<CanvasConfig>(CANVAS_CONFIG);
  private doc = inject<Document>(DOCUMENT);
  private auth = inject(Auth);
  private canvasService = inject(CanvasService);
  private datastore = inject(Datastore);
  private pwa = inject(Pwa);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private modalService = inject(ModalService);
  private toastAlertService = inject(ToastAlertService);

  @Input() boardTitle?: string | undefined;
  @Input() canvasId: number;
  @Input() contextType: string;
  @Input() contextId: number;
  @Input() debug = false;
  /** When true, disables collaboration (no room/sync) and persistence is only at the browser. */
  @Input() isEphemeral = false;

  user: User;
  darkMode = false;
  roomId = '';

  tldrawContainerId = `tldraw-component-${uuid()}`;
  private globalFuncContainerName = `__SECRET_DOM_DO_NOT_USE_FUNC_CONTAINER_${uuid()}`;
  private watermarkObserver?: MutationObserver;
  private WATERMARK_CLASS = '.tl-watermark_SEE-LICENSE';
  private NAVIGATION_PANEL_CLASS = '.tlui-navigation-panel';
  private readonly USER_ACKNOWLEDGEMENT_EVENT_NAME =
    'make_real_user_acknowledged';
  private readonly SHOWCASE_DIALOG_EVENT_NAME = 'showcase_dialog_acknowledged';

  async ngOnInit(): Promise<void> {
    // The entire canvas component is only rendered on the browser
    if (isPlatformBrowser(this.platformId)) {
      // Import is dynamic because pre-rendering throws type errors.
      await import('tldraw-web-component');

      const userId$ = this.auth.getUserId().pipe(filter(userId => !!userId));
      const user$ = this.datastore
        .document<UsersCollection>('users', userId$)
        .valueChanges();
      this.user = await firstValueFrom(user$.pipe(untilDestroyed(this)));

      const container = this.doc.querySelector(`#${this.tldrawContainerId}`);
      if (!container) {
        console.error(`#${this.tldrawContainerId} container not found`);
        return;
      }
      const tldrawElement = this.doc.createElement('tldraw-sync-web-component');

      if (this.debug) {
        tldrawElement.setAttribute('debug', '1');
      }

      if (this.isEphemeral) {
        tldrawElement.setAttribute('is-ephemeral', '1');
      } else {
        const { canvasHost } = this.canvasConfig;
        if (!canvasHost) {
          throw new Error('Canvas host is not defined.');
        }

        /**
         * We're checking if the canvas being accessed has the `PLAYGROUND` context type.
         * Canvas with this context type is only being used in webapp playground in dev,
         * so no need to fetch an access token.
         */
        const isPlayground =
          this.contextType === CanvasContextTypeApi.PLAYGROUND;
        let accessToken = '';
        if (!isPlayground) {
          accessToken = await this.canvasService.fetchAccessToken({
            canvasId: this.canvasId,
          });

          if (!accessToken) {
            throw new Error('Failed to access the canvas');
          }
        }

        tldrawElement.setAttribute('server-uri', canvasHost);

        /**
         * Use old room ID format for playground canvas.
         * If we don't, we might access someone else's canvas if the canvas ID is the same.
         */
        if (isPlayground) {
          this.roomId = `${this.contextType}:${this.contextId}`;
        } else {
          this.roomId = `${this.canvasId}`;
        }
        tldrawElement.setAttribute('room-id', this.roomId);
        tldrawElement.setAttribute(
          'query-params',
          JSON.stringify({
            accessToken,
          }),
        );
      }

      try {
        this.darkMode = await firstValueFrom(
          this.themeService.getTheme().pipe(
            map(theme => theme === 'dark'),
            untilDestroyed(this),
          ),
        );
      } catch (e) {
        console.log(e);
      }
      const getFuncContainer = this.getFuncContainer.bind(this);
      (window as any)[this.globalFuncContainerName] = getFuncContainer;

      tldrawElement.setAttribute(
        'get-func-container',
        this.globalFuncContainerName,
      );
      container.appendChild(tldrawElement);

      this.setupWatermarkObserver();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      delete (window as any)[this.globalFuncContainerName];
      if (this.watermarkObserver) {
        this.watermarkObserver.disconnect();
      }
    }
  }

  private applyWatermarkStyles(): void {
    const watermark = this.doc.querySelector(this.WATERMARK_CLASS);
    if (!watermark) {
      return;
    }

    const element = watermark as HTMLElement;
    if (element.getAttribute('data-mobile') === 'false') {
      const navigationPanel = this.doc.querySelector(
        this.NAVIGATION_PANEL_CLASS,
      );
      let leftPosition = 'var(--space-2)';

      if (navigationPanel) {
        const panelWidth = navigationPanel.getBoundingClientRect().width;
        leftPosition = `calc(${panelWidth}px + var(--space-2))`;
      }

      element.style.setProperty('right', 'auto');
      element.style.setProperty('left', leftPosition);
    }
  }

  private setupWatermarkObserver(): void {
    this.watermarkObserver = new MutationObserver(mutations => {
      let shouldReapplyWatermark = false;

      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          // mutations on watermark
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.classList?.contains(this.WATERMARK_CLASS)) {
                shouldReapplyWatermark = true;
              }

              if (element.querySelector(this.WATERMARK_CLASS)) {
                shouldReapplyWatermark = true;
              }

              if (
                element.classList?.contains(this.NAVIGATION_PANEL_CLASS) ||
                element.querySelector(this.NAVIGATION_PANEL_CLASS)
              ) {
                shouldReapplyWatermark = true;
              }
            }
          });
        }

        if (
          // mutations on navigation panel
          mutation.type === 'attributes' &&
          mutation.target instanceof Element &&
          (mutation.target.classList?.contains(this.NAVIGATION_PANEL_CLASS) ||
            mutation.target.closest(this.NAVIGATION_PANEL_CLASS))
        ) {
          shouldReapplyWatermark = true;
        }
      });

      if (shouldReapplyWatermark) {
        this.applyWatermarkStyles();
      }
    });

    this.watermarkObserver.observe(this.doc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  public getUser(): TldrawWCUserProps {
    return {
      id: this.user.id.toString(),
      name: this.user.username,
      colorScheme: this.darkMode ? 'dark' : 'light',
      isInternalStaff: this.user.isStaff,
    };
  }

  private getFuncContainer(): FuncContainer {
    const makeRealFunc: MakeRealFunc = async (prompt, image, messages) => {
      const formattedMessages = messages.map(message => {
        return {
          type: message.type,
          content: message.text || message.image,
        };
      });
      const response =
        await this.datastore.createDocument<AiSmartCanvasMakeRealCollection>(
          'aiSmartCanvasMakeReal',
          {
            developerPrompt: prompt,
            base64Image: image,
            messages: formattedMessages,
            returnValue: '',
          },
        );

      if (response.status !== 'success') {
        throw new Error('Failed to make it real');
      }
      const newMessage = await firstValueFrom(
        this.datastore
          .document<AiSmartCanvasMakeRealCollection>(
            'aiSmartCanvasMakeReal',
            response.id,
          )
          .valueChanges()
          .pipe(untilDestroyed(this)),
      );
      return {
        id: response.id,
        result: newMessage.returnValue,
      };
    };

    const multiplayerAssetsFunc: FuncContainer['multiplayerAssetsFunc'] =
      () => {
        return {
          upload: async (asset, file) => {
            const data = await this.canvasService.uploadFile({
              file,
              canvasId: this.canvasId,
            });

            asset.meta = {
              uuid: data.fileUuid,
            };
            return {
              src: data.url,
            };
          },
          resolve: async asset => {
            if (typeof asset.meta.uuid !== 'string') {
              throw new Error('Asset meta is not a string');
            }

            const response = await this.canvasService.fetchFile({
              fileUuid: asset.meta.uuid,
              canvasId: this.canvasId,
            });

            if (response.status === 'success') {
              return response.result.url;
            }
            throw new Error('Failed to fetch the file');
          },
        };
      };

    const getUserFunc = this.getUser.bind(this);

    const showPermissionsFunc: ShowPermissionsFunc =
      async (): Promise<void> => {
        const permissions = await firstValueFrom(
          this.datastore
            .collection<PrototyperPermissionsCollection>(
              'prototyperPermissions',
              query =>
                query
                  .where('canvasId', '==', this.canvasId)
                  .where('principalId', '==', this.user.id)
                  .where(
                    'principalType',
                    '==',
                    PrototyperPermissionPrincipalTypeApi.USER,
                  )
                  .limit(1),
            )
            .valueChanges()
            .pipe(untilDestroyed(this)),
        );

        const isUserViewer =
          permissions.length > 0 &&
          permissions[0].accessRole ===
            PrototyperPermissionAccessRoleApi.VIEWER;

        if (isUserViewer) {
          this.toastAlertService.open({
            type: ToastAlertType.ERROR,
            message: $localize`You do not have access to edit permissions for this board.`,
            timeout: 5000,
          });
          return;
        }

        const board = await firstValueFrom(
          this.datastore
            .document<CanvasCollection>('canvas', this.canvasId)
            .valueChanges()
            .pipe(untilDestroyed(this)),
        );

        const modalResponse = await this.modalService
          .open('PrototyperPermissionsModal', {
            inputs: {
              board,
              userId: this.user.id,
            },
            size: ModalSize.SMALL,
            mobileFullscreen: true,
          })
          .afterClosed();

        if (modalResponse === 'success') {
          this.toastAlertService.open({
            type: ToastAlertType.SUCCESS,
            message: $localize`Permissions updated successfully.`,
            timeout: 5000,
          });
        }
      };

    const linkShimFunc = (url: string): void => {
      const sig = sha256(this.auth.getCSRFToken() + url);
      const isNative = this.pwa.isNative();
      if (!isNative) {
        window.open(
          `/users/l.php?url=${encodeURIComponent(url)}&sig=${sig}`,
          '_blank',
        );
      } else {
        this.router.navigate(['/users/l.php'], { queryParams: { url, sig } });
      }
    };

    const getConfigFunc: GetConfigFunc = (destinationURL?: string) => {
      return {
        sig: sha256(`${this.auth.getCSRFToken()}${destinationURL || ''}`),
      };
    };

    const userInteractionsFunc = (): {
      hasUserAcknowledged(): Promise<boolean>;
      userAcknowledge(): void;
    } => {
      return {
        hasUserAcknowledged: async () => {
          const userInteractions$ = this.datastore
            .collection<UserInteractionsCollection>('userInteractions', query =>
              query.where(
                'eventName',
                '==',
                this.USER_ACKNOWLEDGEMENT_EVENT_NAME,
              ),
            )
            .valueChanges()
            .pipe(map(interactions => interactions.length > 0));

          return firstValueFrom(userInteractions$.pipe(untilDestroyed(this)));
        },
        userAcknowledge: () => {
          this.datastore
            .collection<UserInteractionsCollection>('userInteractions')
            .push({
              eventName: this.USER_ACKNOWLEDGEMENT_EVENT_NAME,
            });
        },
      };
    };

    const showcaseInteractionsFunc = (): {
      hasUserAcknowledged(): Promise<boolean>;
      userAcknowledge(): void;
    } => {
      return {
        hasUserAcknowledged: async () => {
          const interactions$ = this.datastore
            .collection<UserInteractionsCollection>('userInteractions', query =>
              query.where('eventName', '==', this.SHOWCASE_DIALOG_EVENT_NAME),
            )
            .valueChanges()
            .pipe(map(interactions => interactions.length > 0));

          return firstValueFrom(interactions$.pipe(untilDestroyed(this)));
        },
        userAcknowledge: () => {
          this.datastore
            .collection<UserInteractionsCollection>('userInteractions')
            .push({
              eventName: this.SHOWCASE_DIALOG_EVENT_NAME,
            });
        },
      };
    };

    const boardFunc: boardFunc = (titleToUpdate?: string) => {
      const currentBoardTitle = this.boardTitle || 'Untitled Prototype';

      if (typeof titleToUpdate !== 'string') {
        return { currentBoardTitle };
      }

      this.datastore
        .collection<CanvasCollection>('canvas')
        .update(this.canvasId, { title: titleToUpdate })
        .then(response => {
          if (response.status !== 'success') {
            throw new Error('Failed to update board title');
          }
        });

      return {
        currentBoardTitle: titleToUpdate,
      };
    };

    return {
      multiplayerAssetsFunc,
      getUserFunc,
      makeRealFunc,
      showPermissionsFunc,
      linkShimFunc,
      getConfigFunc,
      userInteractionsFunc,
      showcaseInteractionsFunc,
      boardFunc,
    };
  }
}
